<template>
  <Loader :value="promises.fetchOne" :message="$metaInfo.title" class="contact-mutate">
    <div class="container view">
      <HeaderComponent :title="$metaInfo.title" :breadcrumb="breadcrumbs">
        <template #right>
          <div class="row wrap g-1">
            <Actions placement="contact" :query="{ contact: id }">
              <!-- <template #bottom>
                <ButtonComponent
                  v-if="!isNew && $store.getters.hasAccess('admin')"
                  :label="$t('delete')"
                  @click="removeDialog = true"
                  :icon="['fal', 'times']"
                />
              </template> -->
            </Actions>

            <ButtonComponent
              :label="isNew ? $t('create') : $t('save')"
              theme="primary"
              type="submit"
              :promise="promises.mutate"
              :icon="['fal', isNew ? 'plus' : 'arrow-to-bottom']"
              :class="{ 'elder-button--disabled': !validator.passes || !hasChangesComp }"
              v-tooltip="{ content: hasChangesComp ? null : $t('noUnsavedChanges') }"
              @click="submit"
            />
          </div>
        </template>
      </HeaderComponent>
      <Alert v-if="existingScope" class="mb-4">
        <ButtonComponent
          v-if="$route.query.returnTo"
          :label="$t('goBack')"
          icon="fa-pro-light:angle-left"
          icon-placement="left"
          class="mr-2"
          tag="router-link"
          :to="$route.query.returnTo"
        />
        <span>{{
          $t('contactScopeAlreadyAddedMessage', { name: item.name, target: existingScope.reference.name })
        }}</span>
      </Alert>
      <div class="contact-mutate__cards">
        <Card
          tag="form"
          :title="$t('contactInformation')"
          :subtitle="$t('contactCardSublabel')"
          @submit.prevent="submit"
          class="contact-mutate__form"
        >
          <template #default>
            <div class="contact-mutate__inputs">
              <InputComponent
                v-model="data.name"
                data-field="name"
                :label="labels.name"
                required
                autofocus
                :is-valid="validator.fields.name.valid"
                :validation-message="validator.fields.name.error"
              />

              <InputComponent
                v-model="data.email"
                data-field="email"
                :label="labels.email"
                type="email"
                required
                :is-valid="validator.fields.email.valid"
                :icon="['fal', 'envelope']"
              >
                <template #validation-message>
                  <span>{{ validator.fields.email.error }}</span>
                  <a v-if="exists" href="#" class="ml-1" @click.prevent="goToExisting">{{
                    $t('openX', { x: $tc('contact', 1).toLowerCase() })
                  }}</a>
                </template>
              </InputComponent>

              <InputComponent
                v-model="data.phone"
                data-field="phone"
                :label="labels.phone"
                type="tel"
                :is-valid="validator.fields.phone.valid"
                :validation-message="validator.fields.phone.error"
                :icon="['fal', 'mobile']"
              />

              <InputComponent
                v-model="data.birthdate"
                data-field="birthdate"
                :label="labels.birthdate"
                :icon="['fal', 'calendar']"
              >
                <InputDate v-model="birthdate" />
              </InputComponent>

              <InputComponent data-field="gender" type="select" :label="labels.gender">
                <SelectComponent
                  v-model="data.gender"
                  :placeholder="$t('select') + '...'"
                  :items="[
                    { value: 'male', label: $t('male') },
                    { value: 'female', label: $t('female') },
                  ]"
                />
              </InputComponent>

              <CheckboxComponent v-model="isCompany" :label="labels.company" />
              <CheckboxComponent v-model="data.upsell" :label="labels.upsell" />

              <Company v-if="data.company" v-model="data.company" :validator="validator" validator-prefix="company" />

              <CustomFields
                data-field="fields"
                :value="data.customFields"
                @input="$ev => $set(data, 'customFields', $ev)"
                :schema="customFieldSchemas"
                :data="data"
                :validator="validator"
                validator-prefix="customFields"
                :placement="['contact']"
              />

              <h2>{{ $t('address') }}</h2>

              <Address
                v-model="data.address"
                :show-map="false"
                data-field="address"
                validator-prefix="address"
                :validator="validator"
                class="my-2"
              />

              <!-- <InputComponent :label="labels.assignees" data-field="assignees">
                <UserSelect
                  v-model="data.assignees"
                  multiple
                  search
                  :items="fetchUsers"
                  :title="$t('selectx', { x: labels.assignees.toLowerCase() })"
                  class="elder-input__element"
              /></InputComponent> -->
            </div>
          </template>
        </Card>

        <Card
          :title="$t('contactLeadsCardLabel')"
          :subtitle="$t('contactLeadsCardSublabel')"
          class="contact-mutate__scopes"
        >
          <LeadScopes v-model="scopes" :initial-current="$route.query.current || $route.query.scope" />
        </Card>
      </div>
      <RemoveComponent
        :show.sync="removeDialog"
        :title="$metaInfo.title"
        :action="() => remove({ id })"
        @onSuccess="
          $router.push({
            name: 'contacts',
          })
        "
        :resource="$tc('contact')"
      />
    </div>
  </Loader>
</template>

<script>
import Validator from '@/mixins/validator-v2'
import ModelMixin from '@/mixins/Model'
import { mapState, mapActions, mapMutations } from 'vuex'
import { DateTransformer } from '@/utils/transformers'
import { mapi18n } from '@/i18n'
import Model from '@/models/Contact'
import CompanyModel from '@/models/Company'
import UserApi from '@/api/User'
import ContactApi from '@/api/Contact'
import { Debounce } from '@/utils'
import { mergeDeepRight } from 'ramda'

import CustomFields, { getValidatorConfig } from '@kvass/custom-fields'
import UserSelect from '@/components/User/Select/Modal'
import SelectComponent from '@/components/Select/Select'
import { InputComponent } from 'vue-elder-input'
import { DropdownComponent } from 'vue-elder-dropdown'
import { CheckboxComponent } from 'vue-elder-checkbox'
import Address from '@/components/Address'
import RemoveComponent from '@/components/Remove'
import HeaderComponent from '@/components/Header'
import Card from '@/components/Card-v2'
import Tags from '@kvass/custom-fields/src/Fields/Tags'
import Actions from '@/components/Actions'
import Company from '@/components/Company'
import Avatar from '@/components/Avatar'
import InputDate from '@/components/InputDate'
import LeadScopes from '@/components/Lead/Scopes.vue'
import { AlertComponent as Alert } from 'vue-elder-alert'

const LeadsToScopes = contact => {
  return (contact.leads || [])
    .filter(({ reference }) => reference.id)
    .flatMap(({ scopes, reference }) =>
      scopes.map(s => {
        const result = { ref: s.id, onModel: s.__typename }
        if (s.__typename !== 'Project') result.parent = reference.id
        return result
      }),
    )
}

const ScopesToLeads = scopes => {
  const projects = Array.from(new Set(scopes.map(s => (s.onModel === 'Project' ? s.ref : s.parent))))

  return projects.map(id => {
    return {
      reference: { id },
      scopes: scopes.filter(s => s.parent === id || s.ref === id).map(s => ({ id: s.ref, __typename: s.onModel })),
    }
  })
}

const getScopesDiff = (n = [], o = []) => {
  const oldIds = o.map(x => x.ref)
  const newIds = n.map(x => x.ref)

  const added = n.filter(x => !oldIds.includes(x.ref))
  const removed = o.filter(x => !newIds.includes(x.ref))

  return { added, removed }
}

export default {
  mixins: [
    Validator({
      rules: 'rules',
      labels: 'labels',
      data: 'data',
    }),
    ModelMixin({
      guard: true,
      model: Model,
      transform: {
        assignees: v => v.map(e => e.id),
      },
    }),
  ],
  props: {
    id: String,
  },
  data() {
    return {
      removeDialog: false,
      scopes: [],
      schemas: [],
      scope: {},
      exists: null,
    }
  },
  watch: {
    id: {
      handler: 'fetch',
      immediate: true,
    },
    item: {
      handler(value) {
        this.data = Model(value)
      },
      immediate: true,
    },
    initialScopes: {
      handler(value) {
        this.scopes = value
      },
      immediate: true,
    },
    'validator.fields.email.error'() {
      this.exists = null
    },
  },
  computed: {
    ...mapState('Contact', ['item', 'promises']),
    ...mapState(['enums']),
    ...mapi18n('i18n', {
      loading() {
        return this.$tc('loading') + '...'
      },
      loadingLead() {
        return this.$t('loading', { resource: this.$tc('contact', 1) }).toLowerCase() + '...'
      },
    }),
    existingScope() {
      if (!this.$route.query.scope) return

      return (this.$path('item.leads') || []).find(x => x.reference.id === this.$route.query.scope)
    },
    initialScopes() {
      const scopes = LeadsToScopes(this.item)
      if (this.scope.resource === 'project') scopes.push({ onModel: 'Project', ref: this.scope.id })
      return scopes
    },
    isNew() {
      return !this.id
    },
    customFieldSchemas() {
      if (!this.schemas.length) return []
      return this.schemas.flatMap(c => c.schema || c)
    },
    customFieldValidatorConfig() {
      if (!this.customFieldSchemas.length) return {}
      return getValidatorConfig(this.customFieldSchemas, this.data, { prefix: 'customFields' })
    },
    labels() {
      return {
        ...mapi18n([
          'email',
          'name',
          'phone',
          'company',
          'birthdate',
          'gender',
          { upsell: 'contactUpsell' },
          { assignees: ['customermanager', 2] },
          { 'address.street': 'street' },
          { 'address.city': 'city' },
          { 'address.postcode': 'postcode' },
          { 'address.county': 'county' },
          { 'company.name': 'companyName' },
          { 'company.organizationNumber': 'orgNo' },
        ]),
        ...this.customFieldValidatorConfig.labels,
      }
    },
    rules() {
      let base = {
        name: 'required',
        email:
          'required|email' +
          (this.isNew
            ? `|emailAvailable:contact:${[this.data.email, this.$path('data.company.organizationNumber')].join(':')}`
            : ''),
        phone: 'mobile',
        'address.street': 'string',
        'address.city': 'string',
        'address.postcode': 'string',
        'address.county': 'string',
        ...this.customFieldValidatorConfig.rules,
      }

      if (Boolean(this.data.company))
        return {
          ...base,
          'company.name': 'required',
          'company.organizationNumber': 'required',
        }

      return base
    },
    breadcrumbs() {
      if (!this.$route.query.scope)
        return [
          { title: this.$tc('contact', 2), action: { name: 'contacts' } },
          { title: this.$metaInfo.title, action: { name: 'contact-dashboard', params: { id: this.id } } },
          { title: this.$t('edit') },
        ]

      const { id, title, resource } = this.scope
      if (!resource) return []

      return [
        {
          title: this.$tc(resource, 2),
          action: { name: `${resource}s` },
        },
        {
          title,
          action: {
            name: `${resource}-dashboard`,
            params: { id },
          },
        },
        {
          title: this.$tc('lead', 2),
          action: {
            name: `${resource}-leads`,
            params: { reference: id },
          },
        },
        { title: this.$metaInfo.title },
      ]
    },
    birthdate: DateTransformer('data.birthdate'),
    isCompany: {
      get() {
        return Boolean(this.data.company)
      },
      set(val) {
        this.$set(this.data, 'company', val ? CompanyModel() : null)
      },
    },
    hasChangesComp() {
      const { added, removed } = getScopesDiff(this.scopes, LeadsToScopes(this.item))

      return this.hasChanges || [...added, ...removed].length
    },
  },
  methods: {
    ...mapActions('Contact', ['mutate', 'remove']),
    ...mapMutations('Contact', ['set']),
    fetch() {
      this.set({})
      this.fetchScope()

      this.fetchSchema()
      if (!this.isNew) this.$store.dispatch('Contact/fetch', { id: this.id })
    },
    fetchSchema() {
      const url = new URL('/api/specs/customfieldschema/contact', window.location.origin)
      if (this.id) url.searchParams.set('id', this.id)
      return fetch(url)
        .then(res => res.json())
        .then(data => (this.schemas = data))
    },
    fetchScope() {
      if (!this.$route.query.scope) return

      return fetch(`/api/resource/project,page?ids=${this.$route.query.scope}&field.id=id&field.title=name`)
        .then(res => res.json())
        .then(d => (this.scope = d[0]))
    },
    submit() {
      if (!this.validator.passes || !this.hasChangesComp) return

      let data = this.getData({ diff: true })

      const scopesDiff = getScopesDiff(this.scopes, LeadsToScopes(this.item))

      this.mutate([
        { id: this.id, ...data },
        {
          strategy: (item, payload) => {
            return {
              ...mergeDeepRight(item, mergeDeepRight(payload, data)),
              leads: ScopesToLeads(this.scopes),
            }
          },
        },
      ]).then(async data => {
        if ([...scopesDiff.added, ...scopesDiff.removed].length) await ContactApi.setScopes(data.id, scopesDiff)

        if (this.$route.query.returnTo) return this.$router.push(this.$route.query.returnTo)

        if (this.isNew)
          this.$router.replace({
            name: 'contact-edit',
            params: { id: data.id },
          })
      })
    },

    fetchUsers(filter) {
      filter.type = 'user'

      return UserApi.get({ filter }, { query: 'UserSearch' })
    },
    async onEmailValidation(data = {}) {
      const { passes, response } = data
      this.exists = passes ? null : await response.text()
    },
    goToExisting() {
      const id = this.exists
      this.set({}) // Turn off guard
      this.$nextTick(() => {
        this.$router.replace({ name: 'contact-edit', query: this.$route.query, params: { id } })
      })
    },
  },
  created() {
    if (this.isNew && this.$path('state.enums.ClientDetails.contract', this.$store) !== 'commercial')
      this.isCompany = true

    this.$bus.on('validator:emailAvailable', this.onEmailValidation)
  },
  beforeDestroy() {
    this.$bus.off('validator:emailAvailable', this.onEmailValidation)
  },
  metaInfo() {
    return {
      title: this.isNew
        ? this.$t(!this.$route.query.scope ? 'createContact' : 'createLead')
        : this.item.name || this.i18n.loadingContact,
    }
  },
  components: {
    InputComponent,
    HeaderComponent,
    DropdownComponent,
    CheckboxComponent,
    RemoveComponent,
    Card,
    UserSelect,
    Tags,
    Actions,
    Company,
    Address,
    Avatar,
    InputDate,
    SelectComponent,
    CustomFields,
    LeadScopes,
    Alert,
  },
}
</script>

<style lang="scss">
.contact-mutate {
  background-color: $background-grey;

  &__form {
    margin: 0 auto;
    max-width: 850px;
  }

  .lead-scopes__pane {
    min-height: 0;
  }

  h2 {
    margin: 1rem 0 -1rem 0;
    grid-column-end: span 2;
  }

  &__thumbnail {
    $size: 150px;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    flex-shrink: 0;
    width: $size;
    height: $size;
    background-size: cover;
    background-position: center;
    background-color: css-alpha('border-color', 0.4);
    border-radius: 50%;
    color: css-darken('border-color', 20%);
  }

  &__inputs {
    display: grid;
    align-items: center;
    grid-template-columns: repeat(2, 1fr);
    grid-gap: 1rem;
    align-items: flex-start;

    .company-form,
    & > [data-field='name'],
    [data-field='tags'],
    [data-field='address'],
    [data-field='assignees'],
    [data-field='fields'] {
      grid-column-end: span 2;
    }
  }

  &__cards {
    display: flex;
    gap: 2rem;

    & > * {
      flex: 1;
    }
  }

  &__scopes {
    // position: absolute;
    // width: 100%;
    // height: 100%;
    // top: 0;
    // left: 0;

    // &-container {
    //   position: relative;
    // }
  }

  [type='submit'] {
    margin-right: auto;
  }
}
</style>
